import { APP_INITIALIZER, ApplicationConfig, provideZoneChangeDetection } from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { providePrimeNG } from 'primeng/config';
import Aura from '@primeng/themes/aura';
import { routes } from './app.routes';
import { provideHttpClient, withInterceptors, withXsrfConfiguration } from '@angular/common/http';
import { csrfInterceptor } from './interceptors/csrf.interceptor';
import { devJwtInterceptor } from './interceptors/dev-jwt.interceptor';
import { httpErrorInterceptor } from './interceptors/http-error.interceptor';
import { sessionExpiryInterceptor } from './interceptors/session-expiry.interceptor';
import { superAdminModuleForbiddenInterceptor } from './interceptors/superadmin-module-forbidden.interceptor';
import { SIMULATE_FAILURE_INTERCEPTORS } from './interceptors/simulate-failure.registration';
import { noCacheInterceptor } from './interceptors/no-cache.interceptor';
import { environment } from '../environments/environment';
import { FilingLoaderMode } from '../environments/environment.types';
import { FILING_LOADER_STRATEGY } from './services/mbrs/loaders/filing-loader.token';
import { FILING_LOADER_VARIANTS } from './services/mbrs/loaders/filing-loader.registry';
import { RuntimeConfigService } from './services/common/runtime-config.service';
import { setLivenessDiagnosticsEnabled } from './services/mbrs/liveness-probe-diagnostics';
import { renderBootError } from './boot-error';

// Resolve the active filing-loader strategy class from the registry at
// composition time. This is the single place `environment.filingLoaderMode`
// is read — every downstream consumer depends on the FILING_LOADER_STRATEGY
// token instead. The cast narrows the widened object-literal property back
// to its validated union type (validated at write-time via `satisfies`).
const activeFilingLoaderStrategy =
  FILING_LOADER_VARIANTS[environment.filingLoaderMode as FilingLoaderMode].strategy;

// Boot-time cache-debug probes. Gated on `RuntimeConfig.cacheDebug` so
// they cost nothing when the flag is off. These surface the three
// "hidden" cache layers that have bitten past deploys:
//   1. The VM's Nginx policy — is `/assets/config.json` actually served
//      with `no-store`? Is `/` (index.html)? A mis-pasted config from
//      deploy/nginx/mbrs-frontend.conf silently re-introduces stale HTML.
//   2. The static `<meta http-equiv="Cache-Control">` — is the right
//      per-env index.html in place? (angular.json swaps it per build.)
//   3. ServiceWorkers — MBRS ships none, but a stale registration from
//      a previous experiment can cache bundles indefinitely.
//   4. Hashed bundle immutability — is the browser re-fetching or
//      reusing from disk cache? `transferSize === 0` means disk cache.
//   5. BFCache — when a page is restored from back-forward cache the
//      app does not re-bootstrap; we log the event so ops can tell.
async function runCacheDebugBootProbes(cfg: RuntimeConfigService): Promise<void> {
  if (!cfg.cacheDebug()) return;

  /* eslint-disable no-console */
  const mainScript = document.querySelector<HTMLScriptElement>('script[src*="main"]')?.src ?? null;
  const metaCacheControl = document
    .querySelector<HTMLMetaElement>('meta[http-equiv="Cache-Control"]')
    ?.content ?? null;

  console.debug('[cache-debug] boot', {
    at: new Date().toISOString(),
    environment: environment.env,
    mainBundle: mainScript,
    indexMetaCacheControl: metaCacheControl,
  });

  try {
    const regs = await navigator.serviceWorker?.getRegistrations?.() ?? [];
    console.debug('[cache-debug] service-workers', {
      count: regs.length,
      scopes: regs.map((registration) => registration.scope),
    });
  } catch {
    // ignored — secure-context only API
  }

  const probe = async (path: string) => {
    try {
      const response = await fetch(path, { cache: 'no-store' });
      return {
        path,
        status: response.status,
        'cache-control': response.headers.get('Cache-Control'),
        age: response.headers.get('Age'),
        etag: response.headers.get('ETag'),
        date: response.headers.get('Date'),
        'x-cache': response.headers.get('X-Cache'),
      };
    } catch (err) {
      return { path, error: err instanceof Error ? err.message : String(err) };
    }
  };
  console.debug('[cache-debug] header probes', {
    config: await probe('/assets/config.json'),
    index: await probe('/'),
  });

  const hashed = performance
    .getEntriesByType('resource')
    .filter((entry): entry is PerformanceResourceTiming => 'transferSize' in entry)
    .filter((entry) => /\.(js|css)$/i.test(new URL(entry.name, location.origin).pathname))
    .map((entry) => ({
      name: entry.name.split('/').pop(),
      transferSize: entry.transferSize,
      decodedBodySize: entry.decodedBodySize,
      fromCache: entry.transferSize === 0 && entry.decodedBodySize > 0,
    }));
  console.debug('[cache-debug] resource timings (hashed bundles)', hashed);

  window.addEventListener('pageshow', (pageShowEvent) => {
    console.debug('[cache-debug] pageshow', { persisted: pageShowEvent.persisted });
  });
  /* eslint-enable no-console */
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideHttpClient(
      // Order matters: httpErrorInterceptor first (network / 5xx sentry),
      // then sessionExpiryInterceptor (single-active-session 401 → forced
      // logout for cookie sessions; skips dev/Bearer + /api/auth/* itself),
      // then devJwtInterceptor attaches Bearer for dev URLs, then
      // csrfInterceptor (which now early-returns when Authorization is
      // present so the two schemes never stack on the same request).
      // noCacheInterceptor runs last so its headers survive regardless of
      // earlier clones — it suppresses browser/proxy caching of API + hub
      // responses so freshly deployed code is reflected immediately.
      // simulateFailureInterceptor (QA switch for the SRV toast) sits right after
      // httpErrorInterceptor so that one sees its synthetic 5xx like a real one;
      // the production build swaps its registration file for an empty list
      // (angular.json fileReplacements), so none of it ships there.
      withInterceptors([
        httpErrorInterceptor,
        ...SIMULATE_FAILURE_INTERCEPTORS,
        sessionExpiryInterceptor,
        superAdminModuleForbiddenInterceptor,
        devJwtInterceptor,
        csrfInterceptor,
        noCacheInterceptor,
      ]),
    ),
    provideAnimationsAsync(),
    providePrimeNG({
        theme: {
            preset: Aura,
            options: {
              darkModeSelector: 'none',
              cssLayer: {
                cssLayer: {
                  name: 'primeng',
                  order: 'theme, base, ,utilities, primeng',
                },
              }
          }
        }
    }),
    { provide: FILING_LOADER_STRATEGY, useClass: activeFilingLoaderStrategy },
    // Fetch and validate /assets/config.json before bootstrap completes.
    // Any rejection here aborts bootstrap — the boot-error panel in
    // index.html is surfaced via main.ts's catch handler.
    {
      provide: APP_INITIALIZER,
      multi: true,
      deps: [RuntimeConfigService],
      useFactory: (cfg: RuntimeConfigService) => () =>
        cfg.load().then(() => {
          // Runtime half of the `seance` liveness-probe gate. Pushed in rather
          // than injected: `liveness-probe-diagnostics.ts` is DI-free by
          // design so it cannot fail the specs that stub the services it
          // observes. The build-time `!environment.production` guard inside
          // that module is the outer half.
          setLivenessDiagnosticsEnabled(cfg.livenessProbeDebug());
          return runCacheDebugBootProbes(cfg);
        }).catch((err) => {
          renderBootError(err);
          throw err;
        }),
    },
  ]
};
